import { getLocalTimeZone } from "@internationalized/date";
import { useMutation } from "@tanstack/react-query";
import { Stop, TrainIdentifier } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { isPast } from "date-fns";
import { InternalMessagesForm } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { InternalMessageRequest } from "features/CenterContent/RoleContent/InternalMessage/schema/requestSchema";
import { internalMessagesFormToRequest } from "features/CenterContent/RoleContent/InternalMessage/schema/transformers";
import { MessageList } from "features/CenterContent/RoleContent/InternalMessage/shared/MessageList";
import { useFormInternalMessage } from "features/CenterContent/RoleContent/InternalMessage/utils/useForm";
import { INTERNAL_EVENTS_QUERY_KEY } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/EventsOverviewButtonAndModal";
import {
  getCurrentCalendarDateTime,
  getNextDayStartOfPlanCalendarDateTime,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/initialDurationValues";
import { BadgeText as BadgeTextStation } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/StationEventModal";
import { BadgeText } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { TrainInternalMessagesQueryKey } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/useTrainInternalMessages";
import { getVisibilityEndTime } from "features/CenterContent/VehicleDetails/TrainDetails/getVisibilityEndTime";
import { useTrainRoute } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { FC } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { ActionModal, delaycloseDuration } from "shared/components/ActionModal";
import { Flex } from "shared/components/Spor";
import { VisibilityInput } from "shared/form/VisibilityInput";
import { VisibilityForm } from "shared/form/visibilitySchema";
import { AllOrNone } from "shared/utils/objectUtils";
import { StretchBuilderModalState } from "stores/useStretchBuilder";

type InternalMessageType = {
  isGlobal: boolean;
  isTrain: boolean;
  isStation: boolean;
};

const getInternalMessageType = (
  train?: TrainIdentifier,
  stations: Stop[] = [],
): InternalMessageType => ({
  isGlobal: !train && stations.length === 0,
  isTrain: train !== undefined,
  isStation: stations.length >= 1,
});

const makeTitle = (
  internalMessageType: InternalMessageType,
  stations: Stop[],
) => {
  const { isGlobal, isTrain } = internalMessageType;
  if (isGlobal) return `Ny global intern melding`;
  if (isTrain) return `Ny intern melding til tog`;
  return `Ny intern melding til ${stations.length >= 2 ? "stasjoner" : "stasjon"}`;
};

type StationProps = {
  stations?: Stop[];
  allStopsSelected?: boolean;
  stretchBuilderModalState?: StretchBuilderModalState;
  setStretchBuilderModalState?: (value: StretchBuilderModalState) => void;
  onClose?: () => void;
};

type CreateInternalMessageModalProps = {
  setModalOpen: (value: boolean) => void;
  visibility?: VisibilityForm;
  train?: TrainIdentifier;
} & AllOrNone<StationProps>;

/**
 * Creates every internal messages without related events
 * Functionally similiar to InternalMessage.tsx, but has a visibility field and submit button
 *
 * Documentation: https://vygruppen.atlassian.net/wiki/spaces/TRAFFICGUI/pages/edit-v2/7957217376
 */
const CreateInternalMessageDialogContent: FC<
  CreateInternalMessageModalProps
> = ({
  setModalOpen,
  visibility,
  train,
  stations = [],
  allStopsSelected,
  stretchBuilderModalState,
  setStretchBuilderModalState,
  onClose = () => {},
}) => {
  const internalMessageType = getInternalMessageType(train, stations);
  const { isGlobal, isTrain, isStation } = internalMessageType;
  const formMethods = useFormInternalMessage(
    undefined,
    visibility ?? {
      type: "TIMED",
      fromTime: getCurrentCalendarDateTime(),
      toTime: getNextDayStartOfPlanCalendarDateTime(),
    },
  );

  const { handleSubmit, watch } = formMethods;

  const toTime = watch("visibility.toTime");
  const isInThePast = toTime
    ? isPast(toTime.toDate(getLocalTimeZone()))
    : false;
  const submitDisabled = isInThePast;
  const submitDisabledTooltip = isTrain
    ? "Kan ikke opprette intern melding. Toget har fullført sin rute."
    : "Synlighetsdatoen kan ikke være i fortiden.";
  const submitTooltip = submitDisabled ? submitDisabledTooltip : undefined;

  const { mutate: submitRequest, status: submitStatus } = useMutation({
    mutationFn: (data: InternalMessageRequest) =>
      mutationFnPOST<undefined, InternalMessageRequest>(
        `${getBackendUrl()}/global-information/create`,
        data,
      ),
    onSuccess: () => {
      /**
       * Delay by the same duration as actionModal,
       * else it would flicker on createInternalMessages
       * since it is updated before the actionModal is closed.
       */
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: [INTERNAL_EVENTS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: TrainInternalMessagesQueryKey(
            train?.nominalDate,
            train?.identifier,
            train?.countryCode,
          ),
        });
      }, delaycloseDuration);
    },
  });

  const onSubmit: SubmitHandler<InternalMessagesForm> = (form) => {
    if (!form.visibility) return;
    const request = internalMessagesFormToRequest(
      form.internalMessages,
      form.visibility,
      { relevantTrain: train, relevantStops: stations },
    );
    if (!request) return;
    submitRequest(request);
  };

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        title={makeTitle(internalMessageType, stations)}
        titleBadge={isGlobal ? "Uavhengig" : undefined}
        actionTitle="Send melding"
        onClose={() => {
          setModalOpen(false);
          onClose();
        }}
        onSubmit={handleSubmit(onSubmit)}
        onSubmitTooltip={submitTooltip}
        isSubmitDisabled={submitDisabled}
        loading={submitStatus === "pending"}
        isSuccess={submitStatus === "success"}
        isError={submitStatus === "error"}
        successMessage="Melding sendt"
        failureMessage="Kunne ikke sende melding. Prøv på nytt, eller kontakt IT hvis feilen vedvarer"
        noOverflow
      >
        <ErrorBoundary>
          <Flex flexDir="column" gap="30px" w="100%">
            {isTrain && <BadgeText selectedTrain={train!} />}
            {isStation && (
              <BadgeTextStation
                selectedStops={stations}
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- eslint doesn't understand the type
                allStopsSelected={allStopsSelected!}
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                stretchBuilderModalState={stretchBuilderModalState!}
                setStretchBuilderModalState={() => {
                  // Workaround for navigating back to stretchBuilder
                  setStretchBuilderModalState!({
                    type: "station",
                    stretchBuilderMode: true,
                  });
                  setModalOpen(false);
                }}
              />
            )}
            <MessageList />
            {!isTrain && (
              <VisibilityInput
                visibilityField="visibility"
                isRelatedToIncident={false}
                subField="toTime"
              />
            )}
          </Flex>
        </ErrorBoundary>
      </ActionModal>
    </FormProvider>
  );
};

const CreateInternalMessageModal: FC<CreateInternalMessageModalProps> = ({
  train,
  ...props
}) => {
  const trainRoute = useTrainRoute(train ?? {});
  if (train && !trainRoute.data) return null;
  const trainVisibility = {
    type: "TIMED",
    fromTime: getCurrentCalendarDateTime(),
    toTime: getVisibilityEndTime(trainRoute.data?.stops ?? []),
  } as const;

  return (
    <CreateInternalMessageDialogContent
      train={train}
      visibility={train ? trainVisibility : undefined} // for !isTrain you pick the visibility yourself
      {...props}
    />
  );
};

export default CreateInternalMessageModal;
